<svelte:options runes={false} />
<script>
  // Allocation tab (U14): per-org allocation + per-org NAV/PnL, with add/withdraw cash per org and add/remove an
  // accessible org — wired to the U7 202 endpoints. Apply is ASYNC (KTD4), so each adjustment shows a
  // Pending → Applied/Failed lifecycle driven by the SSE adjustment-status event (U10) with a light poll
  // fallback. A LIVE capital-increase rejection (synchronous, from U7) surfaces immediately — no Pending row.
  //
  // The pending-status + SSE-update model is chosen over optimistic UI so a Failed apply-time rejection (the
  // KTD4 re-check) shows HONESTLY rather than reverting a fake-applied state. Destructive actions (withdraw-all /
  // remove-org, which flatten positions) use a confirm dialog.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { targetOf } from '../../../lib/runContext.js';
  import { selectedOrgId, availableOrgs } from '../../../lib/orgContext.js';
  import {
    selectedAdjustments, recordPendingAdjustment, setAdjustmentsFromServer,
  } from '../../../lib/autotradeStore.js';
  import { formatCurrency } from '../../../lib/store.js';
  import { formatQuoteAmount } from '../../../lib/quoteCurrency.js';

  export let strategy = null;
  export let status = null;
  export let isLive = false;

  let slices = [];
  let loading = false;
  let error = null;
  let actionError = null;
  let busy = false;
  let pollTimer = null;

  $: strategyId = strategy?.strategyId ?? null;

  // ---- Live balance ceiling (#2): a LIVE capital increase is capped client-side at the account's unreserved
  //      USD — the same slider+textbox-per-org model the Live deploy form uses (the server's U6/U7 balance
  //      gate re-checks it authoritatively). A failed balance read degrades to free entry (server still gates).
  let freeUsd = null;
  let balanceLoading = false;

  async function loadBalance() {
    if (!isLive) return;
    balanceLoading = true;
    try {
      // ADR-0014 (review fix): the balance figures must be IN the strategy's own quote currency — the
      // USD read false-blocked a rich-USDC increase and trivially passed a BTC one.
      // The balance is the LIVE account's on the strategy's own venue (ADR-0040 — never a `?? 2` guess: a
      // snapshot with no classifiable target degrades to free entry and the server re-checks on accept).
      const venue = targetOf(strategy)?.venue ?? null;
      if (venue === null) { freeUsd = null; return; }
      const res = await api.getAvailableBalance(venue, $selectedOrgId, strategy?.quoteCurrency ?? 'USD');
      freeUsd = res?.freeUsd ?? null;
    } catch {
      freeUsd = null; // degrade to free entry; the server re-checks on accept.
    } finally {
      balanceLoading = false;
    }
  }

  // Per-org slider bound: the unreserved balance, floored to whole dollars ($10 step granularity).
  $: sliderMax = isLive && freeUsd != null ? Math.max(0, Math.floor(freeUsd)) : null;

  // The accessible orgs not already participating — the add-org picker source.
  $: participatingIds = new Set(slices.map((s) => s.orgId));
  $: addableOrgs = $availableOrgs.filter((o) => !participatingIds.has(o.orgId));

  async function loadSlices() {
    if (!strategyId) return;
    loading = true;
    error = null;
    const at = $selectedOrgId;
    try {
      const [rows, adj] = await Promise.all([
        api.getOrgSlices(strategyId, at),
        api.getAdjustments(strategyId, at),
      ]);
      // Drop a stale-org response (org-switch race guard).
      if ($selectedOrgId !== at || strategy?.strategyId !== strategyId) return;
      slices = rows ?? [];
      setAdjustmentsFromServer(adj ?? []);
    } catch (err) {
      error = mapError(err);
    } finally {
      loading = false;
    }
  }

  // ---- Per-org cash actions. Each returns 202 + adjustmentId; we record it Pending and let the SSE status
  //      event (or the poll fallback) flip it. A synchronous 4xx (live over-ceiling) surfaces as actionError. ----
  let cashInputs = {};   // orgId -> numeric amount ('' when empty)
  let cashDisplays = {}; // orgId -> the formatted text shown in the input ($1,234 style, no symbol)

  function formatCash(n) {
    if (n === null || n === undefined || n === '' || isNaN(n)) return '';
    return Number(n).toLocaleString('en-US');
  }
  function onCashInput(orgId, event) {
    const digits = event.target.value.replace(/[^\d]/g, '');
    cashInputs[orgId] = digits === '' ? '' : Number(digits);
    cashDisplays[orgId] = event.target.value;
  }
  function onCashBlur(orgId) {
    cashDisplays[orgId] = formatCash(cashInputs[orgId]);
  }
  // Slider + text input stay in sync (the deploy form's per-org slider model, Live only).
  function onCashSlide(orgId, event) {
    const v = Number(event.target.value);
    cashInputs[orgId] = v;
    cashDisplays[orgId] = formatCash(v);
  }
  function clearCashInput(orgId) {
    cashInputs[orgId] = '';
    cashDisplays[orgId] = '';
  }

  async function submit(fn, { orgId, kind, amount } = {}) {
    if (busy) return;
    busy = true;
    actionError = null;
    try {
      const out = await fn();
      if (out?.adjustmentId) {
        recordPendingAdjustment(out.adjustmentId, { requestId: strategyId, orgId, kind, amount });
      }
      // Refetch slices after a moment so the NAV/slice reflects an applied change (the SSE refetch also covers it).
      schedulePoll();
    } catch (err) {
      actionError = mapError(err);
    } finally {
      busy = false;
    }
  }

  function addCash(orgId) {
    const amount = Number(cashInputs[orgId]);
    if (!(amount > 0)) { actionError = 'Enter a positive amount to add.'; return; }
    // Live ceiling (#2): a capital INCREASE must fit the unreserved balance (mirrors the deploy form; the
    // server's balance gate re-checks authoritatively on accept).
    if (isLive && freeUsd != null && amount > freeUsd) {
      actionError = `Amount exceeds your unreserved balance (${formatQuoteAmount(freeUsd, strategy?.quoteCurrency ?? 'USD')}).`;
      return;
    }
    clearCashInput(orgId);
    return submit(() => api.addCash(strategyId, orgId, amount, $selectedOrgId), { orgId, kind: 'AddCash', amount });
  }
  function withdraw(orgId) {
    const amount = Number(cashInputs[orgId]);
    if (!(amount > 0)) { actionError = 'Enter a positive amount to withdraw.'; return; }
    clearCashInput(orgId);
    return submit(() => api.withdraw(strategyId, orgId, amount, $selectedOrgId), { orgId, kind: 'Withdraw', amount });
  }

  // ---- Destructive actions go through a confirm dialog (withdraw-all / remove-org flatten positions). ----
  let confirmOpen = false;
  let confirmKind = null; // 'withdrawAll' | 'removeOrg'
  let confirmOrg = null;
  function openConfirm(kind, orgId) { confirmKind = kind; confirmOrg = orgId; confirmOpen = true; }
  function closeConfirm() { confirmOpen = false; confirmKind = null; confirmOrg = null; }
  function confirmAction() {
    const orgId = confirmOrg;
    const kind = confirmKind;
    closeConfirm();
    if (kind === 'withdrawAll') {
      return submit(() => api.withdrawAll(strategyId, orgId, $selectedOrgId), { orgId, kind: 'WithdrawAll' });
    }
    // Re-check the ≥1-org invariant at fire time — a concurrent refetch (the poll fallback) may have reduced the
    // slice set to one while the confirm dialog was open. The server also enforces this, but don't fire a remove
    // the client's own invariant means to prevent.
    if (slices.length <= 1) {
      actionError = 'A strategy must keep at least one participating org.';
      return;
    }
    return submit(() => api.removeOrg(strategyId, orgId, $selectedOrgId), { orgId, kind: 'RemoveOrg' });
  }

  // ---- Add an accessible, non-participating org. ----
  let addOrgId = '';
  let addOrgCash = 1_000_000;
  let addOrgCashEdited = false;

  // Live: default the new org's slice to the unreserved balance (the deploy form's default) until edited.
  $: if (isLive && sliderMax != null && !addOrgCashEdited) addOrgCash = sliderMax;

  function onAddOrgCashInput(event) {
    addOrgCashEdited = true;
    addOrgCash = Number(event.target.value);
  }
  function onAddOrgCashSlide(event) {
    addOrgCashEdited = true;
    addOrgCash = Number(event.target.value);
  }
  function addOrg() {
    if (!addOrgId || !(Number(addOrgCash) > 0)) { actionError = 'Pick an org and a positive starting cash.'; return; }
    const startingCash = Number(addOrgCash);
    // Live ceiling (#2): a new live slice is a capital increase — cap it at the unreserved balance.
    if (isLive && freeUsd != null && startingCash > freeUsd) {
      actionError = `Starting cash exceeds your unreserved balance (${formatQuoteAmount(freeUsd, strategy?.quoteCurrency ?? 'USD')}).`;
      return;
    }
    const orgId = addOrgId;
    addOrgId = '';
    addOrgCash = isLive && sliderMax != null ? sliderMax : 1_000_000;
    addOrgCashEdited = false;
    return submit(() => api.addOrg(strategyId, { orgId, startingCash }, $selectedOrgId), { orgId, kind: 'AddOrg', amount: startingCash });
  }

  // Poll fallback (SSE may drop): re-pull slices + adjustments a couple of times after an action so a Pending
  // flips even without the push. The host's SSE refetch covers the live case; this is the belt-and-suspenders.
  function schedulePoll() {
    clearTimeout(pollTimer);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      await loadSlices();
      await loadBalance(); // an applied live adjustment changes the reservation → refresh the ceiling.
      if (attempts < 3) pollTimer = setTimeout(tick, 2500);
    };
    pollTimer = setTimeout(tick, 2500);
  }

  function orgName(orgId) {
    return $availableOrgs.find((o) => o.orgId === orgId)?.orgName ?? orgId;
  }
  function mapError(err) {
    const msg = String(err?.message ?? err);
    const idx = msg.indexOf('details: ');
    return idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
  }

  // Reload when the selected strategy changes.
  let lastId = null;
  $: if (strategyId && strategyId !== lastId) { lastId = strategyId; loadSlices(); loadBalance(); }

  onMount(() => { loadSlices(); loadBalance(); });
  onDestroy(() => clearTimeout(pollTimer));
</script>

<div class="at-allocation-tab">
  {#if loading && slices.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadSlices}>Retry</button>
    </div>
  {:else}
    {#if actionError}
      <div class="alert alert-danger" role="alert">{actionError}</div>
    {/if}

    <!-- Live: the unreserved-balance ceiling every capital increase is capped at (mirrors the deploy form). -->
    {#if isLive}
      <div class="at-alloc-balance" role="status">
        {#if balanceLoading && freeUsd == null}
          <span class="form-hint">Reading your available balance…</span>
        {:else if freeUsd != null}
          <span class="at-alloc-balance-label">Unreserved balance</span>
          <span class="at-alloc-balance-value">{formatQuoteAmount(freeUsd, strategy?.quoteCurrency ?? 'USD')}</span>
          <span class="form-hint">— capital increases are capped at this amount.</span>
        {:else}
          <span class="form-hint">Balance unavailable — amounts are re-checked against your account on accept.</span>
        {/if}
      </div>
    {/if}

    <!-- Per-org allocation rows. -->
    <table class="at-alloc-table">
      <thead>
        <tr>
          <th scope="col">Organization</th>
          <th scope="col" class="num">Allocated</th>
          <th scope="col" class="num">NAV</th>
          <th scope="col" class="num">Realized P&amp;L</th>
          <th scope="col" class="at-alloc-actions-col">Manage</th>
        </tr>
      </thead>
      <tbody>
        {#each slices as s (s.orgId)}
          <tr>
            <td class="at-alloc-org">{orgName(s.orgId)}</td>
            <td class="num">{formatQuoteAmount(s.startingCash, strategy?.quoteCurrency ?? 'USD')}</td>
            <td class="num">{s.nav != null ? formatQuoteAmount(s.nav, strategy?.quoteCurrency ?? 'USD') : '—'}</td>
            <td class="num" class:pos={s.realizedPnl > 0} class:neg={s.realizedPnl < 0}>
              {s.realizedPnl != null ? formatCurrency(s.realizedPnl) : '—'}
            </td>
            <td class="at-alloc-actions">
              <div class="at-alloc-actions-row">
                <div class="at-alloc-cashwrap">
                  <span class="at-alloc-cashsym">$</span>
                  <input type="text" inputmode="numeric" class="form-input at-alloc-cash-input"
                    placeholder="amount" value={cashDisplays[s.orgId] ?? ''}
                    on:input={(e) => onCashInput(s.orgId, e)} on:blur={() => onCashBlur(s.orgId)}
                    aria-label={`Cash amount for ${orgName(s.orgId)}`}
                    aria-invalid={isLive && freeUsd != null && Number(cashInputs[s.orgId]) > freeUsd} />
                </div>
                <button type="button" class="btn btn-sm btn-primary"
                  disabled={busy || (isLive && freeUsd != null && Number(cashInputs[s.orgId]) > freeUsd)}
                  title={isLive && freeUsd != null && Number(cashInputs[s.orgId]) > freeUsd
                    ? `Exceeds your unreserved balance (${formatQuoteAmount(freeUsd, strategy?.quoteCurrency ?? 'USD')})` : 'Add cash to this org'}
                  on:click={() => addCash(s.orgId)}>Add</button>
                <button type="button" class="btn btn-sm btn-ghost" disabled={busy} on:click={() => withdraw(s.orgId)}>Withdraw</button>
                <button type="button" class="btn btn-sm btn-ghost" disabled={busy} on:click={() => openConfirm('withdrawAll', s.orgId)}>Withdraw all</button>
                <button type="button" class="btn btn-sm btn-danger" disabled={busy || slices.length <= 1}
                  title={slices.length <= 1 ? 'A strategy must keep at least one participating org.' : 'Remove this org'}
                  on:click={() => openConfirm('removeOrg', s.orgId)}>Remove</button>
              </div>
              <!-- Per-org amount slider (Live): bounded by the unreserved balance, the deploy form's model. -->
              {#if sliderMax != null}
                <div class="at-alloc-sliderwrap">
                  <input type="range" min="0" max={sliderMax} step="10"
                    value={Number(cashInputs[s.orgId]) || 0} disabled={busy}
                    on:input={(e) => onCashSlide(s.orgId, e)}
                    aria-label={`Cash amount slider for ${orgName(s.orgId)}`} />
                  <span class="at-alloc-sliderpct">
                    {sliderMax > 0 ? Math.round(((Number(cashInputs[s.orgId]) || 0) / sliderMax) * 100) : 0}%
                  </span>
                </div>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>

    <!-- Add a non-participating accessible org. -->
    <div class="at-add-org">
      <h5>Add an organization</h5>
      {#if addableOrgs.length === 0}
        <p class="form-hint">All your accessible organizations are already participating.</p>
      {:else}
        <div class="at-add-org-row">
          <select class="form-input" bind:value={addOrgId} aria-label="Organization to add">
            <option value="">Select an org…</option>
            {#each addableOrgs as o (o.orgId)}
              <option value={o.orgId}>{o.orgName ?? o.orgId}{o.email ? ` · ${o.email}` : ''}</option>
            {/each}
          </select>
          <input type="number" min="0" step="1000" class="form-input" value={addOrgCash}
            on:input={onAddOrgCashInput} aria-label="Starting cash"
            aria-invalid={isLive && freeUsd != null && Number(addOrgCash) > freeUsd} />
          <button type="button" class="btn btn-primary"
            disabled={busy || !addOrgId || (isLive && freeUsd != null && Number(addOrgCash) > freeUsd)}
            title={isLive && freeUsd != null && Number(addOrgCash) > freeUsd
              ? `Exceeds your unreserved balance (${formatQuoteAmount(freeUsd, strategy?.quoteCurrency ?? 'USD')})` : 'Add this org'}
            on:click={addOrg}>Add org</button>
        </div>
        {#if sliderMax != null}
          <div class="at-alloc-sliderwrap at-add-org-slider">
            <input type="range" min="0" max={sliderMax} step="10" value={Number(addOrgCash) || 0}
              disabled={busy} on:input={onAddOrgCashSlide} aria-label="Starting cash slider for the new org" />
            <span class="at-alloc-sliderpct">
              {sliderMax > 0 ? Math.round(((Number(addOrgCash) || 0) / sliderMax) * 100) : 0}%
            </span>
          </div>
        {/if}
        {#if isLive}
          <p class="form-hint">Adding a live slice re-checks the account's available USD before it's accepted.</p>
        {/if}
      {/if}
    </div>

    <!-- Pending / recent adjustments strip (the async-apply lifecycle). -->
    {#if $selectedAdjustments.length}
      <div class="at-pending-section">
        <h5>Recent adjustments</h5>
        <ul class="at-pending-strip" aria-label="Adjustment status">
          {#each $selectedAdjustments.slice(0, 8) as a (a.id)}
            <li class="at-pending-item at-pending-{String(a.status).toLowerCase()}">
              <span class="at-pending-kind">{a.kind}{a.orgId ? ` · ${orgName(a.orgId)}` : ''}</span>
              {#if a.amount != null}<span class="at-pending-amount">{formatCurrency(a.amount)}</span>{/if}
              <span class="at-pending-status">{a.status}{a.failureReason ? ` — ${a.failureReason}` : ''}</span>
            </li>
          {/each}
        </ul>
      </div>
    {/if}
  {/if}
</div>

{#if confirmOpen}
  <div class="at-modal-backdrop" on:click|self={closeConfirm}>
    <div class="at-modal" role="dialog" aria-modal="true" aria-labelledby="at-alloc-confirm-title">
      <h3 id="at-alloc-confirm-title" class="at-modal-title">
        {confirmKind === 'withdrawAll' ? 'Withdraw all cash' : 'Remove organization'}
      </h3>
      <p class="at-modal-body">
        {confirmKind === 'withdrawAll'
          ? `Withdraw ${orgName(confirmOrg)}'s entire attributed balance. The strategy flattens that org's proportional position.`
          : `Remove ${orgName(confirmOrg)} from this strategy. Its proportional position is flattened; the strategy continues for the remaining orgs.`}
      </p>
      <div class="at-modal-actions">
        <button type="button" class="btn btn-ghost" on:click={closeConfirm}>Cancel</button>
        <button type="button" class="btn btn-danger" on:click={confirmAction}>
          {confirmKind === 'withdrawAll' ? 'Withdraw all' : 'Remove org'}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  @import '../../Backtesting/Backtesting.css';
  .at-alloc-table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
  .at-alloc-table th, .at-alloc-table td { padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); text-align: left; }
  .at-alloc-table th.num, .at-alloc-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .at-alloc-org { font-weight: 600; }
  .num.pos { color: var(--success, #1aa260); }
  .num.neg { color: var(--danger, #e0533a); }
  .at-alloc-actions { display: flex; flex-direction: column; gap: 0.35rem; align-items: stretch; }
  .at-alloc-actions-row { display: flex; gap: 0.3rem; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
  .at-alloc-cashwrap { display: flex; align-items: center; gap: 0.2rem; }
  .at-alloc-cashsym { color: var(--text-muted, #8a8b8d); font-size: 0.85rem; }
  .at-alloc-cash-input { width: 7rem; text-align: right; font-variant-numeric: tabular-nums; }
  .at-alloc-sliderwrap { display: flex; align-items: center; gap: 0.6rem; }
  .at-alloc-sliderwrap input[type='range'] { flex: 1; accent-color: var(--accent, #3c78f0); }
  .at-alloc-sliderpct { font-size: 0.75rem; color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; width: 2.6rem; text-align: right; }
  .at-add-org-slider { margin-top: 0.4rem; }
  .at-alloc-balance { display: flex; align-items: baseline; gap: 0.45rem; margin-bottom: 0.6rem; }
  .at-alloc-balance-label { font-size: 0.78rem; color: var(--text-secondary, #b5b6b8); }
  .at-alloc-balance-value { font-size: 0.9rem; font-weight: 700; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }
  .at-add-org { margin-bottom: 1rem; }
  .at-add-org h5, .at-pending-section h5 { margin: 0 0 0.4rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .at-add-org-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .at-pending-strip { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  .at-pending-item { display: flex; gap: 0.75rem; align-items: center; padding: 0.35rem 0.6rem; border-radius: 5px; font-size: 0.78rem; background: var(--surface-1, rgba(255, 255, 255, 0.03)); }
  .at-pending-pending { border-left: 3px solid var(--warning, #d8a000); }
  .at-pending-applied { border-left: 3px solid var(--success, #1aa260); }
  .at-pending-failed { border-left: 3px solid var(--danger, #e0533a); }
  .at-pending-kind { font-weight: 600; }
  .at-pending-amount { font-variant-numeric: tabular-nums; }
  .at-pending-status { margin-left: auto; color: var(--text-muted, #8a8b8d); font-variant-numeric: tabular-nums; }
</style>
